import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios, { AxiosRequestConfig } from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import config from '../config';
import {
  AdminDataGrid,
  AdminFilterBar,
  AdminPageHeader,
  DataGridColumn,
  DataGridRowAction,
  FilterConfig,
  HeaderAction,
  SortDirection,
} from './components/AdminMasterDetail';
import {
  AdminBreadcrumbs,
  AdminSectionHeading,
  AdminStatCard,
  ConfirmDialog,
} from './components/AdminUiPrimitives';
import {
  IconArchive,
  IconBook,
  IconCheckCircle,
  IconDelete,
  IconGeneral,
  IconMetadata,
  IconSpark,
} from './components/AdminIcons';

interface PlatformUserRole {
  id: number;
  userId: number;
  auth0Sub: string;
  email: string;
  roleKey: string;
  assignedAt: string;
}

const PLATFORM_ROLE_OPTIONS = [
  { value: 'SuperAdmin', label: 'Super Admin' }
];

type SortKey = 'user' | 'role' | 'assigned';

interface PlatformUserRoleRow {
  id: number;
  user: string;
  role: string;
  assigned: string;
  platformRole: PlatformUserRole;
}

const DEFAULT_PAGE_SIZE = 10;

const ManagePlatformUserRoles: React.FC = () => {
  const { isAuthenticated, loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const apiBaseUrl = `${config.apiBaseUrl}/api/organizations`;

  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [platformUserRoles, setPlatformUserRoles] = useState<PlatformUserRole[]>([]);
  const [targetSub, setTargetSub] = useState('');
  const [targetEmail, setTargetEmail] = useState('');
  const [roleKey, setRoleKey] = useState('SuperAdmin');
  const [searchValue, setSearchValue] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('assigned');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [selectedPlatformRoleId, setSelectedPlatformRoleId] = useState<number | null>(null);
  const [mobilePane, setMobilePane] = useState<'list' | 'details'>('list');
  const [pendingRemoveId, setPendingRemoveId] = useState<number | null>(null);

  const isSmallScreen = viewportWidth < 768;
  const isNarrowLayout = viewportWidth < 1140;

  const clearMessages = () => {
    setError('');
    setNotice('');
  };

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getManagementToken = useCallback(async (): Promise<string> => {
    const tokenResponse = await getAccessTokenSilently().catch(() =>
      getAccessTokenSilently({ cacheMode: 'off' } as any)
    );

    const token =
      typeof tokenResponse === 'string'
        ? tokenResponse
        : (tokenResponse as { access_token?: string })?.access_token;

    if (!token) {
      throw new Error('Unable to get access token. Please sign in again and retry.');
    }

    return token;
  }, [getAccessTokenSilently]);

  const withAuth = useCallback(
    async (request: AxiosRequestConfig) => {
      const token = await getManagementToken();
      return axios({
        ...request,
        headers: {
          ...(request.headers ?? {}),
          Authorization: `Bearer ${token}`
        }
      });
    },
    [getManagementToken]
  );

  const loadPlatformUserRoles = useCallback(async () => {
    try {
      setLoading(true);
      clearMessages();

      const response = await withAuth({
        method: 'get',
        url: `${apiBaseUrl}/platform-user-roles`
      });

      setPlatformUserRoles((response.data ?? []) as PlatformUserRole[]);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to load platform user roles.';
      setError(message);
      setPlatformUserRoles([]);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, withAuth]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    loadPlatformUserRoles();
  }, [isAuthenticated, loadPlatformUserRoles]);

  const handleAssignPlatformRole = async () => {
    if (!targetSub.trim() && !targetEmail.trim()) {
      setError('Auth0 sub or email is required for platform roles.');
      return;
    }

    try {
      setSaving(true);
      clearMessages();

      await withAuth({
        method: 'post',
        url: `${apiBaseUrl}/platform-user-roles`,
        data: {
          auth0Sub: targetSub.trim() || null,
          email: targetEmail.trim() || null,
          roleKey
        }
      });

      setNotice('Platform role assigned.');
      setTargetSub('');
      setTargetEmail('');
      await loadPlatformUserRoles();
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to assign platform role.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleRemovePlatformRole = async (platformUserRoleId: number) => {
    try {
      setSaving(true);
      clearMessages();

      await withAuth({
        method: 'delete',
        url: `${apiBaseUrl}/platform-user-roles/${platformUserRoleId}`
      });

      setNotice('Platform role removed.');
      await loadPlatformUserRoles();
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to remove platform role.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const rows = useMemo<PlatformUserRoleRow[]>(
    () =>
      platformUserRoles.map((platformRole) => ({
        id: platformRole.id,
        user: platformRole.email || platformRole.auth0Sub || `User #${platformRole.userId}`,
        role: platformRole.roleKey,
        assigned: platformRole.assignedAt,
        platformRole,
      })),
    [platformUserRoles]
  );

  const filteredRows = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    if (!query) {
      return rows;
    }

    return rows.filter((row) => [row.user, row.role, row.assigned].join(' ').toLowerCase().includes(query));
  }, [rows, searchValue]);

  const sortedRows = useMemo(() => {
    const direction = sortDirection === 'asc' ? 1 : -1;
    return [...filteredRows].sort((left, right) => {
      const leftValue = String(left[sortKey]).toLowerCase();
      const rightValue = String(right[sortKey]).toLowerCase();
      if (leftValue < rightValue) {
        return -1 * direction;
      }
      if (leftValue > rightValue) {
        return 1 * direction;
      }
      return 0;
    });
  }, [filteredRows, sortDirection, sortKey]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / DEFAULT_PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * DEFAULT_PAGE_SIZE;
    return sortedRows.slice(start, start + DEFAULT_PAGE_SIZE);
  }, [page, sortedRows]);

  const selectedPlatformRole = useMemo(
    () => platformUserRoles.find((platformRole) => platformRole.id === selectedPlatformRoleId) ?? null,
    [platformUserRoles, selectedPlatformRoleId]
  );

  useEffect(() => {
    if (!selectedPlatformRole && platformUserRoles.length > 0) {
      setSelectedPlatformRoleId(platformUserRoles[0].id);
      return;
    }

    if (!platformUserRoles.length) {
      setSelectedPlatformRoleId(null);
    }
  }, [platformUserRoles, selectedPlatformRole]);

  const setSelectedRow = (platformRole: PlatformUserRole) => {
    setSelectedPlatformRoleId(platformRole.id);
    if (isSmallScreen) {
      setMobilePane('details');
    }
  };

  const handleSort = (columnKey: string) => {
    if (!['user', 'role', 'assigned'].includes(columnKey)) {
      return;
    }

    const nextKey = columnKey as SortKey;
    if (sortKey === nextKey) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortKey(nextKey);
    setSortDirection('asc');
  };

  const columns = useMemo<DataGridColumn<PlatformUserRoleRow>[]>(() => [
    {
      key: 'user',
      label: 'User',
      sortable: true,
      width: isSmallScreen ? '56%' : '38%',
      render: (row) => (
        <div style={{ minWidth: isSmallScreen ? 0 : 180 }}>
          <div style={{ fontWeight: 700, color: '#142541', overflowWrap: 'anywhere' }}>{row.user}</div>
          <div style={{ fontSize: '0.75rem', color: '#607399', marginTop: 2 }}>{row.platformRole.auth0Sub}</div>
        </div>
      ),
    },
    {
      key: 'role',
      label: 'Role',
      sortable: true,
      width: isSmallScreen ? '20%' : '16%',
      render: (row) => row.role,
    },
    {
      key: 'assigned',
      label: 'Assigned',
      sortable: true,
      width: isSmallScreen ? '24%' : '20%',
      render: (row) => new Date(row.assigned).toLocaleString(),
    },
  ], [isSmallScreen]);

  const rowActions: DataGridRowAction<PlatformUserRoleRow>[] = [
    {
      key: 'remove',
      label: 'Remove',
      icon: <IconDelete size={14} />,
      tone: 'destructive',
      onClick: (row) => setPendingRemoveId(row.platformRole.id),
      isDisabled: () => saving,
    },
  ];

  const filterConfigs = useMemo<FilterConfig[]>(() => [
    {
      key: 'role',
      label: 'Role',
      value: roleKey,
      onChange: (value) => setRoleKey(value),
      options: PLATFORM_ROLE_OPTIONS.map((option) => ({ value: option.value, label: option.label })),
    },
  ], [roleKey]);

  const actions: HeaderAction[] = [
    {
      key: 'refresh',
      label: 'Refresh',
      variant: 'secondary',
      onClick: () => { void loadPlatformUserRoles(); },
      disabled: saving,
    },
  ];

  const totalRoles = platformUserRoles.length;
  const superAdminCount = platformUserRoles.filter((role) => role.roleKey === 'SuperAdmin').length;
  const withEmailCount = platformUserRoles.filter((role) => Boolean(role.email)).length;
  const withSubCount = platformUserRoles.filter((role) => Boolean(role.auth0Sub)).length;

  const showListPanel = !isSmallScreen || mobilePane === 'list';
  const showDetailsPanel = !isSmallScreen || mobilePane === 'details';

  const selectedRow = useMemo(
    () => selectedPlatformRole ? rows.find((row) => row.id === selectedPlatformRole.id) ?? null : null,
    [rows, selectedPlatformRole]
  );

  if (!isAuthenticated) {
    return (
      <div style={{ maxWidth: 720, margin: '32px auto', padding: 16 }}>
        <h2 style={{ marginBottom: 12 }}>Platform User Roles</h2>
        <p style={{ marginBottom: 16 }}>Please sign in to manage platform roles.</p>
        <button
          onClick={() => loginWithRedirect()}
          style={{
            background: '#2563eb',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '10px 16px',
            cursor: 'pointer',
            fontWeight: 600
          }}
        >
          Sign In
        </button>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', minHeight: 'calc(100vh - 260px)', display: 'grid', gap: 12 }}>
      <AdminBreadcrumbs
        items={[
          { label: 'Administration', to: '/analytics' },
          { label: 'Platform User Roles', isCurrent: true },
        ]}
      />

      <AdminPageHeader
        title="Platform User Roles"
        subtitle={`${totalRoles} platform roles • ${superAdminCount} super admins`}
        actions={actions}
      />

      {(error || notice) && (
        <div
          style={{
            borderRadius: 10,
            border: `1px solid ${error ? '#fecaca' : '#bfdbfe'}`,
            background: error ? '#fff1f2' : '#eff6ff',
            color: error ? '#b91c1c' : '#1d4ed8',
            padding: '10px 12px',
            fontSize: '0.84rem',
          }}
        >
          {error || notice}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10 }}>
        <AdminStatCard label="Roles" value={totalRoles} icon={<IconBook size={15} />} />
        <AdminStatCard label="Super Admins" value={superAdminCount} icon={<IconCheckCircle size={15} />} accent="active" />
        <AdminStatCard label="With Email" value={withEmailCount} icon={<IconMetadata size={15} />} accent="draft" />
        <AdminStatCard label="With Sub" value={withSubCount} icon={<IconArchive size={15} />} accent="archived" />
      </div>

      <AdminFilterBar
        searchValue={searchValue}
        onSearchChange={(value) => setSearchValue(value)}
        searchPlaceholder="Search by user, role, or assigned time..."
        filters={filterConfigs}
      />

      {isSmallScreen && (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid #d8e3f6', borderRadius: 11, background: '#f6f9ff', padding: 4, width: 'fit-content' }}>
          <button type="button" className={`amd-btn ${mobilePane === 'list' ? 'amd-btn-primary' : 'amd-btn-ghost'}`} onClick={() => setMobilePane('list')}>
            List
          </button>
          <button type="button" className={`amd-btn ${mobilePane === 'details' ? 'amd-btn-primary' : 'amd-btn-ghost'}`} onClick={() => setMobilePane('details')}>
            Details
          </button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: isNarrowLayout ? '1fr' : 'minmax(420px, 58%) minmax(320px, 42%)', gap: 12, minHeight: isNarrowLayout ? 'auto' : 560 }}>
        {showListPanel && (
          <div style={{ minHeight: 0 }}>
            {loading ? (
              <div style={{ border: '1px solid #dfe8f8', borderRadius: 12, padding: 22, background: '#fff' }}>Loading platform roles...</div>
            ) : (
              <AdminDataGrid<PlatformUserRoleRow>
                rows={pagedRows}
                columns={columns}
                selectedRowId={selectedPlatformRoleId}
                onRowClick={(row) => setSelectedRow(row.platformRole)}
                selectedIds={selectedIds}
                onSelectedIdsChange={setSelectedIds}
                showSelection={!isSmallScreen}
                sortKey={sortKey}
                sortDirection={sortDirection}
                onSort={handleSort}
                page={page}
                pageSize={DEFAULT_PAGE_SIZE}
                totalCount={sortedRows.length}
                onPageChange={setPage}
                emptyMessage="No platform roles found."
                rowActions={rowActions}
              />
            )}
          </div>
        )}

        {showDetailsPanel && (
          <div style={{ border: '1px solid #dfe8f8', borderRadius: 12, background: '#fff', boxShadow: '0 8px 18px rgba(18, 42, 80, 0.08)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid #edf2fb' }}>
              {isSmallScreen && (
                <button type="button" className="amd-btn amd-btn-ghost" style={{ marginBottom: 8 }} onClick={() => setMobilePane('list')}>
                  Back to List
                </button>
              )}
              <h3 style={{ margin: 0, width: '100%', boxSizing: 'border-box', fontSize: '1.02rem', fontWeight: 800, color: '#173159', background: selectedPlatformRole ? 'linear-gradient(180deg, #f5f9ff 0%, #ebf3ff 100%)' : 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)', border: selectedPlatformRole ? '1px solid #cfe0fb' : '1px solid #d9e2ef', borderRadius: 10, padding: '9px 11px' }}>
                {selectedPlatformRole ? `Role for ${selectedPlatformRole.email || selectedPlatformRole.auth0Sub || `User #${selectedPlatformRole.userId}`}` : 'Assign Platform Role'}
              </h3>
            </div>

            <div style={{ padding: 14, overflowY: 'auto', minHeight: 0 }}>
              <div style={{ display: 'grid', gap: 18 }}>
                <section style={{ display: 'grid', gap: 12, paddingBottom: 14, borderBottom: '1px solid #edf2fb' }}>
                  <AdminSectionHeading icon={<IconGeneral size={14} />} title="Assign Platform Role" />
                  <div style={{ display: 'grid', gap: 8, gridTemplateColumns: isSmallScreen ? '1fr' : 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) auto' }}>
                    <input type="text" value={targetSub} onChange={(event) => setTargetSub(event.target.value)} placeholder="Auth0 sub" />
                    <input type="text" value={targetEmail} onChange={(event) => setTargetEmail(event.target.value)} placeholder="Email" />
                    <select value={roleKey} onChange={(event) => setRoleKey(event.target.value)}>
                      {PLATFORM_ROLE_OPTIONS.map((role) => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                      ))}
                    </select>
                    <button type="button" className="amd-btn amd-btn-primary" onClick={() => { void handleAssignPlatformRole(); }} disabled={saving}>
                      Assign Platform Role
                    </button>
                  </div>
                </section>

                {selectedPlatformRole && selectedRow && (
                  <section style={{ display: 'grid', gap: 12, paddingBottom: 14, borderBottom: '1px solid #edf2fb' }}>
                    <AdminSectionHeading icon={<IconMetadata size={14} />} title="Selected Role" />
                    <div style={{ display: 'grid', gap: 6, fontSize: '0.82rem', color: '#586f96' }}>
                      <div>User: <strong>{selectedRow.user}</strong></div>
                      <div>Auth0 Sub: <strong>{selectedPlatformRole.auth0Sub}</strong></div>
                      <div>Email: <strong>{selectedPlatformRole.email || '-'}</strong></div>
                      <div>Role: <strong>{selectedPlatformRole.roleKey}</strong></div>
                      <div>Assigned: <strong>{new Date(selectedPlatformRole.assignedAt).toLocaleString()}</strong></div>
                    </div>
                    <button type="button" className="amd-btn amd-btn-ghost" onClick={() => setPendingRemoveId(selectedPlatformRole.id)} disabled={saving}>
                      Remove Role
                    </button>
                  </section>
                )}

                {!selectedPlatformRole && (
                  <section style={{ display: 'grid', gap: 12 }}>
                    <AdminSectionHeading icon={<IconSpark size={14} />} title="Getting Started" />
                    <div style={{ minHeight: 140, border: '1px dashed #c9d6ef', borderRadius: 12, background: '#f8fbff', display: 'grid', placeContent: 'center', textAlign: 'center', color: '#4f658f', gap: 8, padding: 16 }}>
                      <div style={{ color: '#35568a', display: 'inline-flex', justifySelf: 'center' }}><IconCheckCircle size={24} /></div>
                      <div style={{ fontWeight: 700 }}>Select a platform role from the list</div>
                    </div>
                  </section>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={pendingRemoveId != null}
        title="Remove platform role"
        message="Remove this platform role assignment? This action cannot be undone."
        onClose={() => setPendingRemoveId(null)}
        actions={[
          { label: 'Cancel', variant: 'default', onClick: () => setPendingRemoveId(null), disabled: saving },
          {
            label: 'Remove',
            variant: 'destructive',
            disabled: saving || pendingRemoveId == null,
            onClick: () => {
              if (pendingRemoveId == null) {
                return;
              }

              void handleRemovePlatformRole(pendingRemoveId);
              setPendingRemoveId(null);
            },
          },
        ]}
      />
    </div>
  );
};

export default ManagePlatformUserRoles;
